import { AttributeOption } from '@mark43/cad-api';
import {
    differenceInDays,
    differenceInHours,
    differenceInMinutes,
    differenceInMonths,
    differenceInSeconds,
    differenceInYears,
    format,
    parseISO,
    setHours,
    setMinutes,
} from 'date-fns';

import { capitalize, isString, lowerCase } from 'lodash';
import { globalAttributes } from '../global-attributes';
import { months, daysOfWeek } from '../constants';
import { electronBridge } from '../../electron-bridge/electron-bridge';

// ====================
// Time

export function convertHoursMinutesStringToISOString(hoursMinutesString?: string): string {
    if (!hoursMinutesString || hoursMinutesString.indexOf(':') === -1) {
        return '';
    }
    const [hours, minutes] = hoursMinutesString.split(':');
    const hoursNumber = parseInt(hours, 10);
    const minutesNumber = parseInt(minutes, 10);
    let date = new Date();
    date = setHours(date, hoursNumber);
    date = setMinutes(date, minutesNumber);
    return date.toISOString();
}

export function formatHoursMinutes(dateString: string): string {
    const date = new Date(dateString);
    return format(date, 'HH:mm');
}

export function formatHoursMinutesSeconds(dateString: string): string {
    const date = new Date(dateString);
    return format(date, 'HH:mm:ss');
}

export function formatShortTime(date: Date | number | string, formatStr = 'HH:mm'): string {
    if (date) {
        return format(isString(date) ? new Date(date) : date, formatStr);
    }

    return '';
}

// ====================
// Date/Time

export function formatDate(date: string): string {
    return format(parseISO(date), 'MMM d, y');
}

export function formatMonthDayYear(dateString: string): string {
    const date = parseISO(dateString);
    return format(date, `MM/dd/y`);
}

export function formatMonthDayYearHoursMinutes(date: Date | number | string): string {
    if (date) {
        return format(isString(date) ? parseISO(date) : date, 'MM/dd/yy HH:mm');
    }

    return '';
}

export function formatMonthDayYearHoursMinutesSeconds(dateString: string): string {
    const date = new Date(dateString);
    return format(date, 'MM/dd/yy HH:mm:ss');
}

export function formatMonthDayHoursMinutes(dateString: string): string {
    const date = new Date(dateString);
    return format(date, 'LL/dd\xa0HH:mm');
}

export function getIsoDateInUserTimezone(dateInIsoFormat: string) {
    const date = new Date(dateInIsoFormat);
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;

    return new Date(date.getTime() - userTimezoneOffset).toISOString().substring(0, 19);
}

export function getCurrentDateAndTime() {
    const now = new Date();
    const hours = (now.getHours() < 10 ? '0' : '') + now.getHours();
    const minutes = (now.getMinutes() < 10 ? '0' : '') + now.getMinutes();

    return {
        now,
        hours,
        minutes,
        time: `${hours}:${minutes}`,
        day: now.getDate(),
        dayOfWeekNumber: now.getDay(),
        dayOfWeek: daysOfWeek[now.getDay()],
        monthNumber: now.getMonth() + 1,
        month: months[now.getMonth()],
        year: now.getFullYear(),
    };
}

// ====================
// Phone

// ====================
// String

export function snakeCaseToTitleCase(str: string): string {
    return str.split('_').map(capitalize).join(' ');
}

export function snakeCaseToLowerCase(str: string): string {
    return str.split('_').map(lowerCase).join(' ');
}

export function naivePluralize(noun: string, count: number) {
    if (count === 1) {
        return noun;
    } else {
        return `${noun}s`;
    }
}

/**
 * Truncate text with ellipsis
 * @param text - The text to truncate
 * @param maxLength - Maximum length
 * @returns Truncated text with "..." suffix if needed
 */
export function truncateText(text: string, maxLength: number): string {
    if (text.length <= maxLength) {
        return text;
    }
    return `${text.substring(0, maxLength - 3)}...`;
}

/**
 * Truncate file name while preserving extension
 * @param fileName - The file name to truncate
 * @param maxLength - Maximum length (default: 32)
 * @returns Truncated file name with extension preserved
 */
export function truncateFileName(fileName: string, maxLength: number = 32): string {
    if (fileName.length <= maxLength) {
        return fileName;
    }

    const lastDotIndex = fileName.lastIndexOf('.');
    if (lastDotIndex > 0 && fileName.length - lastDotIndex <= 8) {
        const extension = fileName.substring(lastDotIndex);
        const nameWithoutExt = fileName.substring(0, lastDotIndex);
        const availableLength = maxLength - extension.length - 3; // 3 for "..."

        if (availableLength > 0) {
            return `${nameWithoutExt.substring(0, availableLength)}...${extension}`;
        }
    }

    return `${fileName.substring(0, maxLength - 3)}...`;
}

// ====================
// data structure

/**
 * Transforms a list of numbers to an object with numbers as keys mapped to `true`.
 * Useful for fast lookups of whether an item exists in a list.
 */
export function toBooleanMap(list: number[]): { [value: number]: boolean } {
    return list.reduce((acc, val) => {
        acc[val] = true;
        return acc;
    }, {} as { [value: number]: boolean });
}

/**
 * takes 2 dates and returns the largest unit of time that divides them with a custom label
 */
export function getLargestUnitOfTimeOfDurationWithLabel({ start, end }: Interval): string {
    let duration;

    try {
        duration = {
            seconds: differenceInSeconds(end, start),
            minutes: differenceInMinutes(end, start),
            hours: differenceInHours(end, start),
            days: differenceInDays(end, start),
            months: differenceInMonths(end, start),
            years: differenceInYears(end, start),
        };
    } catch (e) {
        duration = {};
    }

    // the Duration object will return a value of 0
    // for the fields that do not have values
    const { years, months, days, hours, minutes, seconds } = duration;

    if (years) {
        return `${years} yr`;
    }
    if (months) {
        return `${months} mo`;
    }
    if (days) {
        return `${days} d`;
    }
    if (hours) {
        return `${hours} hr`;
    }
    if (minutes) {
        return `${minutes} min`;
    }
    // the seconds field is the only one where displaying 0 is valid
    if (seconds || seconds === 0) {
        return `${seconds} sec`;
    }
    // if all of the Duration fields are undefined
    // then something is presumably wrong with one of the Date inputs
    return 'unknown';
}

export function convertMetersToFeet(meters: number) {
    return meters * 3.28084;
}

export function convertFeetToMeters(feet: number) {
    return feet * 0.3048;
}

export function convertRadiansToDegrees(x: number) {
    return (x * 180.0) / Math.PI;
}

export function convertDegreesToRadians(x: number) {
    return (x * Math.PI) / 180.0;
}

// There might be situations when user enters a feet and inches height value with more
// than 12 inches (for instance: 615). The following function aims to fix those values
// so that the inches that exceed 12 will be properly converted to feet, thus showing
// the correct height (for instance: 615 will be converted to 703).
export function convertToFeetInchesNumericFormat(rawValue: number) {
    const lastTwoDigitsOfValue = rawValue % 100;

    if (lastTwoDigitsOfValue < 12) {
        return rawValue;
    }

    const additionalFeet = Math.floor(lastTwoDigitsOfValue / 12);
    const correctedInches = lastTwoDigitsOfValue % 12;

    return rawValue - lastTwoDigitsOfValue + additionalFeet * 100 + correctedInches;
}

export function metersToFeetRoundedForDisplay(meters: number, fractionDigits = 2): number {
    const distanceInFeet = convertMetersToFeet(meters);
    // the + removes any trailing zeroes
    return +distanceInFeet.toFixed(fractionDigits);
}

export function getAttributePriority(riskLevel?: AttributeOption) {
    const level =
        Object.values(globalAttributes.riskLevelGlobal)
            .reverse()
            .findIndex((parentAttributeId) => parentAttributeId === riskLevel?.parentAttributeId) +
        1;

    return level > 4 ? 4 : 1;
}

const getWindowIfBrowserOrRenderer = () => {
    return typeof window === 'undefined' ? undefined : window;
};
export async function reloadApp(forceReloadWeb = false) {
    if (electronBridge && 'isElectronRewrite' in electronBridge && 'restartApp' in electronBridge) {
        await electronBridge.restartApp();
    } else {
        // never call location.reload in cad-next electron. This will cause one window load a different renderer version than other windows.
        const globalWindow = getWindowIfBrowserOrRenderer();
        const url = globalWindow?.location?.href;
        if (url) {
            if (forceReloadWeb) {
                await fetch(url, {
                    headers: {
                        Pragma: 'no-cache',
                        Expires: '-1',
                        'Cache-Control': 'no-cache',
                    },
                    cache: undefined,
                });
            } else {
                await fetch(url, {
                    headers: {},
                    cache: 'default',
                });
            }
            const globalWindow = getWindowIfBrowserOrRenderer();
            if (globalWindow?.location) {
                globalWindow.location.href = url;
                globalWindow.location.reload();
            }
        }
    }
}

export function clearWindowHistoryState() {
    return window.history.replaceState({}, '');
}

export function getOneSizeSmaller(size?: CommonSize) {
    switch (size) {
        case 'md':
            return 'sm';
        case 'lg':
            return 'md';
        case 'sm':
            return 'xs';
        default:
            return undefined;
    }
}

export type CommonSize = 'xs' | 'sm' | 'md' | 'lg';

export type BasicCommonSize = 'sm' | 'md' | 'lg';

export function getTwoSizesSmaller(size?: CommonSize) {
    return getOneSizeSmaller(getOneSizeSmaller(size));
}

export function getBasicCommonSize(size?: CommonSize): BasicCommonSize | undefined {
    switch (size) {
        case 'xs':
            return 'sm';
        case 'md':
        case 'lg':
        case 'sm':
            return size;
        default:
            return undefined;
    }
}
